import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, authHeaders, getToken } from "@/lib/api";
import { Bell, Check, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { relativeDate } from "@/lib/format";
import { toast } from "sonner";

// Short attention alert (beep + vibration) when a new notification arrives while the app is open.
function playAlert() {
  try {
    if (navigator.vibrate) navigator.vibrate([180, 90, 180]);
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [880, 1180].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t = now + i * 0.16;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(t); osc.stop(t + 0.16);
    });
    setTimeout(() => ctx.close && ctx.close(), 800);
  } catch (_) {}
}

// In-app notification inbox for a client or vendor (uses /{role}/inbox).
export default function InboxBell({ role }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const prevUnread = useRef(null);

  const base = role === "vendor" ? "/vendor/inbox" : "/client/inbox";
  const hdr = () => authHeaders(role);

  const load = useCallback(async () => {
    if (!getToken(role)) return;
    try {
      const r = await api.get(base, { headers: hdr() });
      const u = r.data.unread || 0;
      setItems(r.data.notifications || []);
      if (prevUnread.current !== null && u > prevUnread.current) playAlert();
      prevUnread.current = u;
      setUnread(u);
      try {
        if (u > 0 && navigator.setAppBadge) navigator.setAppBadge(u);
        else if (navigator.clearAppBadge) navigator.clearAppBadge();
      } catch (_) {}
    } catch (_) {}
  }, [role]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!getToken(role)) return null;

  const markRead = async () => {
    try { await api.post(`${base}/read`, {}, { headers: hdr() }); setUnread(0); prevUnread.current = 0; try { navigator.clearAppBadge && navigator.clearAppBadge(); } catch (_) {} } catch (_) {}
  };

  const clearAll = async () => {
    try {
      await api.post(`${base}/clear`, {}, { headers: hdr() });
      setItems([]); setUnread(0); prevUnread.current = 0;
      try { navigator.clearAppBadge && navigator.clearAppBadge(); } catch (_) {}
    } catch (_) { toast.error("Errore"); }
  };

  const respondAmount = async (claimId, action) => {
    try {
      await api.post(`/client/claims/${claimId}/amount/respond`, { action }, { headers: authHeaders("client") });
      toast.success(action === "confirm" ? "Spesa confermata!" : "Segnalato come non corretto");
      await markRead();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <Popover onOpenChange={async (o) => { if (o) { await markRead(); load(); } }}>
      <PopoverTrigger asChild>
        <button data-testid={`inbox-bell-${role}`} className="relative h-10 w-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center shrink-0 transition-colors">
          <Bell className="h-5 w-5 text-slate-600" />
          {unread > 0 && (
            <span data-testid="inbox-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full kn-gradient text-white text-[10px] font-extrabold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="z-[1500] w-80 p-0 rounded-2xl overflow-hidden" data-testid={`inbox-panel-${role}`}>
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
          <p className="font-extrabold text-slate-900">Notifiche</p>
          {items.length > 0 && (
            <div className="flex items-center gap-2">
              <button onClick={markRead} data-testid="inbox-read-all" className="text-xs font-bold text-[#E63946]">Leggi tutte</button>
              <span className="text-slate-200">·</span>
              <button onClick={clearAll} data-testid="inbox-clear-all" className="text-xs font-bold text-slate-400 hover:text-[#DC2626]">Cancella</button>
            </div>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-slate-400 text-sm">
              <Bell className="h-7 w-7 mx-auto mb-2 opacity-40" /> Nessuna notifica.
            </div>
          ) : (
            items.map((n) => {
              const isAmount = role === "client" && n.type === "amount_confirm" && n.data?.claim_id;
              return (
                <div key={n.notification_id} data-testid={`inbox-item-${n.notification_id}`}
                  className={`px-4 py-3 border-b border-slate-50 ${!n.read ? "bg-[#FFF7F8]" : ""}`}>
                  <button onClick={() => n.link && navigate(n.link)} className="w-full text-left">
                    <p className="font-bold text-slate-800 text-sm">{n.title}</p>
                    {n.body && <p className="text-xs text-slate-500 mt-0.5">{n.body}</p>}
                    <p className="text-[11px] text-slate-400 mt-1">{relativeDate(n.created_at)}</p>
                  </button>
                  {isAmount && (
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => respondAmount(n.data.claim_id, "confirm")} data-testid={`inbox-confirm-${n.notification_id}`}
                        className="flex-1 h-8 rounded-lg bg-emerald-600 text-white text-xs font-bold flex items-center justify-center gap-1"><Check className="h-3.5 w-3.5" /> Confermo</button>
                      <button onClick={() => respondAmount(n.data.claim_id, "ignore")} data-testid={`inbox-reject-${n.notification_id}`}
                        className="flex-1 h-8 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center gap-1"><X className="h-3.5 w-3.5" /> Non corretto</button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
